/**
 * OrganizationDetailPage — full ORG-3 relation UI:
 *  - Members: add user + orgRole + roleIds; assign roles (roles service)
 *  - Groups: create/attach user groups, manage their members
 *  - Projects: create / link existing projects to the org
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, FolderKanban, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetOrganizationFullQuery,
  useAddOrgMemberMutation,
  useRemoveOrgMemberMutation,
  useGetUserGroupsQuery,
  useCreateUserGroupMutation,
  useGetUserGroupMembersQuery,
  useAddUserGroupMemberMutation,
  useRemoveUserGroupMemberMutation,
} from '@/store/api/organizationsApi';
import { useGetProjectsQuery, useUpdateProjectMutation } from '@/store/api/projectsApi';
import { useGetRolesQuery } from '@/store/api/rolesApi';
import { useGetUsersQuery } from '@/store/api/apiSlice';
import type { OrgMember, UserGroup, OrgRoleName } from '@/types';
import { formatDateSafe } from '@/lib/date';

export function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: orgFull, isLoading } = useGetOrganizationFullQuery(orgId!);
  const { data: users } = useGetUsersQuery({ page: 1, pageSize: 100 });
  const { data: roles } = useGetRolesQuery();
  const { data: projects } = useGetProjectsQuery();

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState<string | null>(null);
  const [linkProjectOpen, setLinkProjectOpen] = useState(false);
  const [removeMember, setRemoveMember] = useState<OrgMember | null>(null);

  const [removeOrgMemberAction] = useRemoveOrgMemberMutation();

  const userList = ((users as { data?: Array<{ _id: string; username: string; email?: string }> } | undefined)?.data ?? []);
  const roleList = (roles as Array<{ _id: string; name: string }> | undefined) ?? [];
  const projectList = (projects as Array<{ _id: string; name: string; orgId?: string }> | undefined) ?? [];

  const orgMemberList = orgFull?.members ?? [];
  const orgProjectList = projectList.filter((p) => p.orgId === orgId);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/organizations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{orgFull?.name ?? 'Organization'}</h1>
          <p className="text-sm text-muted-foreground">
            {orgFull?.description ?? `Manage members, groups, and projects for this organization.`}
          </p>
        </div>
      </div>

      {isLoading && !orgFull ? (
        <LoadingSpinner className="py-12" />
      ) : (
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members"><Users className="mr-1.5 h-3.5 w-3.5" /> Members ({orgMemberList.length})</TabsTrigger>
            <TabsTrigger value="groups"><UserCog className="mr-1.5 h-3.5 w-3.5" /> Groups</TabsTrigger>
            <TabsTrigger value="projects"><FolderKanban className="mr-1.5 h-3.5 w-3.5" /> Projects ({orgProjectList.length})</TabsTrigger>
          </TabsList>

          {/* ── Members ── */}
          <TabsContent value="members" className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setAddMemberOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Add Member
              </Button>
            </div>
            <Card>
              <CardContent className="pt-5">
                {orgMemberList.length === 0 ? (
                  <EmptyState title="No members" description="Add the first member to this organization" />
                ) : (
                  <div className="space-y-2">
                    {orgMemberList.map((m) => {
                      const user = userList.find((u) => u._id === m.userId);
                      return (
                        <div key={m.userId} className="flex items-center justify-between rounded-md border px-3 py-2">
                          <div>
                            <p className="text-sm font-medium">{user?.username ?? m.userId}</p>
                            <p className="text-xs text-muted-foreground">{user?.email}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <Badge variant="secondary" className="text-[10px]">{m.orgRole}</Badge>
                              {(m.roleIds ?? []).map((rid) => (
                                <Badge key={rid} variant="outline" className="text-[10px] font-mono">
                                  {roleList.find((r) => r._id === rid)?.name ?? rid.slice(0, 8)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => setRemoveMember(m)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Groups ── */}
          <TabsContent value="groups" className="space-y-3">
            <GroupManager
              orgId={orgId!}
              userList={userList}
              open={groupOpen}
              onOpen={setGroupOpen}
            />
          </TabsContent>

          {/* ── Projects ── */}
          <TabsContent value="projects" className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setLinkProjectOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Link Project
              </Button>
            </div>
            <Card>
              <CardContent className="pt-5">
                {orgProjectList.length === 0 ? (
                  <EmptyState title="No projects" description="Link or create projects for this organization" />
                ) : (
                  <div className="space-y-2">
                    {orgProjectList.map((p) => (
                      <button
                        key={p._id}
                        className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left hover:bg-muted/40"
                        onClick={() => navigate(`/projects/${p._id}`)}
                      >
                        <span className="text-sm font-medium">{p.name}</span>
                        <span className="text-xs text-muted-foreground">→ details</span>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Add member dialog */}
      <AddMemberDialog
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        orgId={orgId!}
        userList={userList}
        roleList={roleList}
        existing={orgMemberList.map((m) => m.userId)}
        onSuccess={() => toast.success('Member added')}
      />

      {/* Link / create project dialog */}
      <Dialog open={linkProjectOpen} onOpenChange={setLinkProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Project</DialogTitle>
            <DialogDescription>
              Attach an existing project to this organization.
            </DialogDescription>
          </DialogHeader>
          <LinkProjectForm
            orgId={orgId!}
            projectList={projectList}
            onDone={() => setLinkProjectOpen(false)}
            onSuccess={() => toast.success('Project linked')}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeMember}
        onOpenChange={(open) => !open && setRemoveMember(null)}
        title="Remove Member"
        description={`Remove ${removeMember?.userId} from this organization?`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={async () => {
          if (removeMember) {
            try {
              await removeOrgMemberAction({ id: orgId!, userId: removeMember.userId }).unwrap();
              toast.success('Member removed');
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to remove member');
            }
            setRemoveMember(null);
          }
        }}
      />
    </div>
  );
}

// ─── Add member dialog ────────────────────────────────────────────────────────

function AddMemberDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  userList: Array<{ _id: string; username: string; email?: string }>;
  roleList: Array<{ _id: string; name: string }>;
  existing: string[];
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [addMember, { isLoading }] = useAddOrgMemberMutation();
  const [userId, setUserId] = useState('');
  const [orgRole, setOrgRole] = useState<OrgRoleName>('member');
  const [roleIds, setRoleIds] = useState<string[]>([]);

  const available = props.userList.filter((u) => !props.existing.includes(u._id));

  const toggleRole = (id: string) =>
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  const submit = async () => {
    if (!userId) {
      toast.error('Select a user');
      return;
    }
    try {
      await addMember({ id: props.orgId, userId, orgRole, roleIds: roleIds.length > 0 ? roleIds : undefined }).unwrap();
      props.onSuccess();
      setUserId('');
      setRoleIds([]);
      props.onOpenChange(false);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to add member');
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
          <DialogDescription>Attach a user with an org role and optional role bindings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>User</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">— select user —</option>
              {available.map((u) => (
                <option key={u._id} value={u._id}>{u.username} ({u.email})</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Org role</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={orgRole}
              onChange={(e) => setOrgRole(e.target.value as OrgRoleName)}
            >
              <option value="owner">owner</option>
              <option value="admin">admin</option>
              <option value="member">member</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Roles (optional)</Label>
            {props.roleList.length === 0 ? (
              <p className="text-xs text-muted-foreground">No roles defined yet.</p>
            ) : (
              <div className="space-y-1.5 rounded-md border p-2">
                {props.roleList.map((r) => (
                  <label key={r._id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={roleIds.includes(r._id)}
                      onChange={() => toggleRole(r._id)}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Adding…' : 'Add Member'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Group manager (create + attach + members) ────────────────────────────────

function GroupManager(props: {
  orgId: string;
  userList: Array<{ _id: string; username: string; email?: string }>;
  open: string | null;
  onOpen: (id: string | null) => void;
}) {
  const toast = useToast();
  const { data: groups, isLoading } = useGetUserGroupsQuery({ orgId: props.orgId });
  const [createGroupMutation] = useCreateUserGroupMutation();
  const [createForm, setCreateForm] = useState({ name: '', description: '' });

  const groupList = (groups as UserGroup[] | undefined) ?? [];

  const create = async () => {
    if (!createForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const created = await createGroupMutation({
        name: createForm.name.trim(),
        description: createForm.description.trim() || undefined,
        orgId: props.orgId,
      }).unwrap();
      setCreateForm({ name: '', description: '' });
      props.onOpen((created as UserGroup)._id);
      toast.success('User group created');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to create group');
    }
  };

  return (
    <>
      <div className="rounded-md border p-3">
        <p className="text-sm font-medium mb-2">Create a user group in this org</p>
        <div className="flex gap-2">
          <Input placeholder="Group name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
          <Input placeholder="Description" value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
          <Button onClick={create}><Plus className="mr-1 h-4 w-4" /> Create</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Groups in this organization</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <LoadingSpinner className="py-8" />
          ) : groupList.length === 0 ? (
            <EmptyState title="No groups" description="Create a group above" />
          ) : (
            <div className="space-y-2">
              {groupList.map((g) => (
                <div key={g._id} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{g.name}</p>
                    {g.description && <p className="text-xs text-muted-foreground">{g.description}</p>}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => props.onOpen(g._id)}>
                    Manage members
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {props.open && <GroupMembersDialog group={groupList.find((g) => g._id === props.open) ?? null} userList={props.userList} onOpenChange={() => props.onOpen(null)} />}
    </>
  );
}

function GroupMembersDialog(props: {
  group: UserGroup | null;
  userList: Array<{ _id: string; username: string; email?: string }>;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [addMember] = useAddUserGroupMemberMutation();
  const [removeMember] = useRemoveUserGroupMemberMutation();
  const [userId, setUserId] = useState('');

  const { data: members, isLoading } = useGetUserGroupMembersQuery(props.group?._id ?? '', { skip: !props.group });
  const memberList = (members as Array<{ _id?: string; groupId: string; userId: string; role?: string; addedAt: string }> | undefined) ?? [];

  const userOf = (id: string) => props.userList.find((u) => u._id === id);

  const submit = async () => {
    if (!props.group || !userId) return;
    try {
      await addMember({ id: props.group._id, userId }).unwrap();
      setUserId('');
      toast.success('Member added');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to add member');
    }
  };

  return (
    <Dialog open={!!props.group} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Group members — {props.group?.name}</DialogTitle>
          <DialogDescription>Add or remove members from this user group.</DialogDescription>
        </DialogHeader>
        {props.group && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <select
                className="flex h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="">— select user —</option>
                {props.userList.filter((u) => !memberList.some((m) => m.userId === u._id)).map((u) => (
                  <option key={u._id} value={u._id}>{u.username}</option>
                ))}
              </select>
              <Button onClick={submit} disabled={!userId}><Plus className="mr-1 h-4 w-4" /> Add</Button>
            </div>
            {isLoading ? (
              <LoadingSpinner className="py-6" />
            ) : memberList.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No members yet</p>
            ) : (
              <div className="space-y-1.5">
                {memberList.map((m) => (
                  <div key={m.userId} className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm">
                    <span>{userOf(m.userId)?.username ?? m.userId}</span>
                    <div className="flex items-center gap-2">
                      {m.role && <Badge variant="secondary" className="text-[10px]">{m.role}</Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={async () => {
                          if (!props.group) return;
                          try {
                            await removeMember({ id: props.group._id, userId: m.userId }).unwrap();
                            toast.success('Member removed');
                          } catch (err) {
                            toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to remove');
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Added at: {formatDateSafe(memberList[0]?.addedAt, 'MMM d, yyyy')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Link project form ────────────────────────────────────────────────────────

function LinkProjectForm(props: {
  orgId: string;
  projectList: Array<{ _id: string; name: string; orgId?: string }>;
  onDone: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [updateProject] = useUpdateProjectMutation();
  const [selected, setSelected] = useState('');

  const candidates = props.projectList.filter((p) => !p.orgId || p.orgId === props.orgId);

  const submit = async () => {
    if (!selected) {
      toast.error('Select a project');
      return;
    }
    try {
      await updateProject({ id: selected, body: { orgId: props.orgId } }).unwrap();
      props.onSuccess();
      props.onDone();
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to link project');
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Project</Label>
        <select
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">— select project —</option>
          {candidates.map((p) => (
            <option key={p._id} value={p._id}>{p.name}{p.orgId === props.orgId ? ' (linked)' : ''}</option>
          ))}
        </select>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={props.onDone}>Cancel</Button>
        <Button onClick={submit}>Link Project</Button>
      </DialogFooter>
    </div>
  );
}